import { useState } from 'react';
import { ClipboardList, Briefcase, Users, Lightbulb, BarChart3 } from 'lucide-react';
import type { BackendUserWithPlan } from '../../userService';
import {
    getSubmissions,
    type Submission,
} from '../../mentorshipService';
import { useFetchOnMount } from './useFetchOnMount';
import PendingQueue from './PendingQueue';
import AssessmentReviewer from './AssessmentReviewer';
import MentorProjectsList from './MentorProjectsList';
import MentorClientProfilesEditor from './MentorClientProfilesEditor';
import MentorInsightsCurator from './MentorInsightsCurator';
import MentorEngagementSummaryView from './MentorEngagementSummaryView';
import NotificationsBell from '../NotificationsBell/NotificationsBell';
import './mentorship.css';

type Tab = 'reviews' | 'projects' | 'clients' | 'insights' | 'summary';

type Props = {
    user: BackendUserWithPlan;
};

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'reviews', label: 'Reviews', icon: <ClipboardList size={16} /> },
    { key: 'projects', label: 'Projects', icon: <Briefcase size={16} /> },
    { key: 'clients', label: 'Clients', icon: <Users size={16} /> },
    { key: 'insights', label: 'Insights', icon: <Lightbulb size={16} /> },
    { key: 'summary', label: 'Engagement', icon: <BarChart3 size={16} /> },
];

function MentorReviewDashboard({ user }: Props) {
    const [activeTab, setActiveTab] = useState<Tab>('reviews');
    const [active, setActive] = useState<Submission | null>(null);

    const {
        data: submissions,
        loading,
        refetch: loadSubmissions,
    } = useFetchOnMount<Submission[]>(
        async () => (await getSubmissions()).results,
        [],
    );

    const handleFinalized = () => {
        setActive(null);
        void loadSubmissions();
    };

    return (
        <section className="mentorship-view">
            <header className="mentorship-view__header">
                <div>
                    <h2 className="mentorship-view__title">
                        Coaching · {user.first_name || user.username}
                    </h2>
                    <p className="mentorship-view__subtitle">Your mentor dashboard.</p>
                </div>
                <NotificationsBell />
            </header>

            <nav
                className="mentorship-tabs"
                role="tablist"
                aria-label="Mentor dashboard tabs"
            >
                {TABS.map((t) => {
                    const isActive = activeTab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive ? 'true' : 'false'}
                            className={`mentorship-tab ${isActive ? 'is-active' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            {t.icon} {t.label}
                        </button>
                    );
                })}
            </nav>

            <div className="mentorship-view__content">
                {activeTab === 'reviews' && (
                    <div
                        className="mentorship-stack"
                        data-mode={active ? 'detail' : 'list'}
                    >
                        <div className="mentorship-stack__list">
                            <PendingQueue
                                submissions={submissions}
                                loading={loading}
                                activeId={active?.id ?? null}
                                onSelect={setActive}
                            />
                        </div>
                        <div className="mentorship-stack__detail">
                            {active ? (
                                <AssessmentReviewer
                                    key={active.id}
                                    submission={active}
                                    onBack={() => setActive(null)}
                                    onFinalized={handleFinalized}
                                />
                            ) : (
                                <div className="empty-state">
                                    <h3>Select a submission</h3>
                                    <p>Pick a row from the queue to start the review.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {activeTab === 'projects' && <MentorProjectsList />}
                {activeTab === 'clients' && <MentorClientProfilesEditor />}
                {activeTab === 'insights' && <MentorInsightsCurator />}
                {activeTab === 'summary' && <MentorEngagementSummaryView />}
            </div>
        </section>
    );
}

export default MentorReviewDashboard;
